import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import {
  FiTrendingUp, FiTrendingDown, FiClock, FiAward, FiTarget,
  FiBook, FiCalendar, FiCheckCircle, FiAlertCircle
} from 'react-icons/fi';

interface StudentDashboardProps {
  userId: number;
  onNavigateToPackage?: (packageId: number) => void;
  onNavigateToPurchases?: () => void;
}

interface DashboardData {
  kpis: any;
  subscriptions: any[];
  studyTimeData: any[];
  skills: any;
  subjectProgress: any[];
  recentActivity: any[];
  achievements: any[];
  assignments: any[];
  subscriptionStatus: any;
}

const StudentDashboard: React.FC<StudentDashboardProps> = ({ userId, onNavigateToPackage, onNavigateToPurchases }) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<'week' | 'month' | 'all'>('week');

  useEffect(() => {
    if (userId) {
      fetchDashboardData();
    }
  }, [userId, timeframe]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const ANALYTICS_SERVICE_URL = process.env.REACT_APP_ANALYTICS_SERVICE_URL || 'http://localhost:3016';
      const response = await fetch(`${ANALYTICS_SERVICE_URL}/api/dashboard/user/${userId}?timeframe=${timeframe}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data.dashboard);
      } else {
        throw new Error(data.error || 'Failed to load dashboard');
      }
    } catch (err: any) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
      // Set default empty data to show UI structure
      setDashboardData({
        kpis: {
          coursesEnrolled: { value: 0, change: 0, trend: 'neutral' },
          avgScore: { value: 0, change: 0, trend: 'neutral' },
          studyHours: { value: 0, change: 0, trend: 'neutral' },
          completionRate: { value: 0, change: 0, trend: 'neutral' }
        },
        subscriptions: [],
        studyTimeData: [],
        skills: {
          problemSolving: 0,
          criticalThinking: 0,
          communication: 0,
          collaboration: 0,
          creativity: 0,
          timeManagement: 0
        },
        subjectProgress: [],
        recentActivity: [],
        achievements: [],
        assignments: [],
        subscriptionStatus: { hasActiveSubscription: false }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <p>Loading your dashboard...</p>
      </LoadingContainer>
    );
  }

  if (!dashboardData) {
    return <ErrorContainer>Failed to load dashboard data</ErrorContainer>;
  }

  const { kpis, subscriptions, studyTimeData, skills, subjectProgress, recentActivity, achievements, assignments, subscriptionStatus } = dashboardData;

  // Transform skills data for radar chart
  const skillsData = [
    { skill: 'Problem Solving', value: skills.problemSolving || 0 },
    { skill: 'Critical Thinking', value: skills.criticalThinking || 0 },
    { skill: 'Communication', value: skills.communication || 0 },
    { skill: 'Collaboration', value: skills.collaboration || 0 },
    { skill: 'Creativity', value: skills.creativity || 0 },
    { skill: 'Time Management', value: skills.timeManagement || 0 }
  ];

  return (
    <DashboardContainer>
      {/* Header with timeframe selector */}
      <DashboardHeader>
        <HeaderTitle>Student Dashboard</HeaderTitle>
        <HeaderSubtitle>Track your learning progress and achievements</HeaderSubtitle>
        <TimeframeSelector>
          <TimeframeButton 
            active={timeframe === 'week'} 
            onClick={() => setTimeframe('week')}
          >
            This Week
          </TimeframeButton>
          <TimeframeButton 
            active={timeframe === 'month'} 
            onClick={() => setTimeframe('month')}
          >
            This Month
          </TimeframeButton>
          <TimeframeButton 
            active={timeframe === 'all'} 
            onClick={() => setTimeframe('all')}
          >
            All Time
          </TimeframeButton>
        </TimeframeSelector>
      </DashboardHeader>

      {error && (
        <ErrorBanner>
          <FiAlertCircle /> Using sample data - Connect to backend for real statistics
        </ErrorBanner>
      )}

      {/* KPI Cards Row */}
      <KPIGrid>
        <KPICard 
          clickable={kpis.coursesEnrolled.value > 0}
          onClick={() => {
            if (kpis.coursesEnrolled.value > 0 && subscriptions.length > 0 && onNavigateToPackage) {
              // Navigate to the first enrolled course
              onNavigateToPackage(subscriptions[0].packageId);
            }
          }}
        >
          <KPIIcon><FiBook size={24} /></KPIIcon>
          <KPIContent>
            <KPILabel>Courses Enrolled</KPILabel>
            <KPIValue>{kpis.coursesEnrolled.value}</KPIValue>
            <KPIChange trend={kpis.coursesEnrolled.trend}>
              {kpis.coursesEnrolled.trend === 'up' ? <FiTrendingUp size={14} /> : <FiTrendingDown size={14} />}
              {Math.abs(kpis.coursesEnrolled.change)}% vs last semester
            </KPIChange>
          </KPIContent>
        </KPICard>

        <KPICard>
          <KPIIcon><FiAward size={24} /></KPIIcon>
          <KPIContent>
            <KPILabel>Avg. Score</KPILabel>
            <KPIValue>{kpis.avgScore.value}%</KPIValue>
            <KPIChange trend={kpis.avgScore.trend}>
              {kpis.avgScore.trend === 'up' ? <FiTrendingUp size={14} /> : <FiTrendingDown size={14} />}
              {Math.abs(kpis.avgScore.change)}% vs last month
            </KPIChange>
          </KPIContent>
        </KPICard>

        <KPICard>
          <KPIIcon><FiClock size={24} /></KPIIcon>
          <KPIContent>
            <KPILabel>Study Hours</KPILabel>
            <KPIValue>{kpis.studyHours.value}h</KPIValue>
            <KPIChange trend={kpis.studyHours.trend}>
              {kpis.studyHours.trend === 'up' ? <FiTrendingUp size={14} /> : <FiTrendingDown size={14} />}
              {Math.abs(kpis.studyHours.change)}% this week
            </KPIChange>
          </KPIContent>
        </KPICard>

        <KPICard>
          <KPIIcon><FiTarget size={24} /></KPIIcon>
          <KPIContent>
            <KPILabel>Completion Rate</KPILabel>
            <KPIValue>{kpis.completionRate.value}%</KPIValue>
            <KPIChange trend={kpis.completionRate.trend}>
              {kpis.completionRate.trend === 'up' ? <FiTrendingUp size={14} /> : <FiTrendingDown size={14} />}
              {Math.abs(kpis.completionRate.change)}% vs last month
            </KPIChange>
          </KPIContent>
        </KPICard>
      </KPIGrid>

      {/* Charts Row */}
      <ChartsGrid>
        {/* Daily Study Time Chart */}
        <ChartCard>
          <ChartTitle>Daily Study Time</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={studyTimeData}>
              <defs>
                <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
              />
              <Legend iconType="line" />
              <Area 
                type="monotone" 
                dataKey="hours" 
                stroke="#3b82f6" 
                strokeWidth={3}
                fill="url(#colorHours)"
                name="Study Hours"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Skills Assessment Radar */}
        <ChartCard>
          <ChartTitle>Skills Assessment</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={skillsData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="skill" stroke="#6b7280" fontSize={11} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="#6b7280" fontSize={11} />
              <Radar 
                name="Your Skills" 
                dataKey="value" 
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.6}
                strokeWidth={2}
              />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsGrid>

      {/* Learning Progress by Subject */}
      <FullWidthCard>
        <ChartTitle>Learning Progress by Subject</ChartTitle>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={subjectProgress}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="subject" stroke="#6b7280" fontSize={12} />
            <YAxis stroke="#6b7280" fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" />
            <Bar dataKey="inProgress" stackId="a" fill="#3b82f6" name="In Progress" />
            <Bar dataKey="notStarted" stackId="a" fill="#9ca3af" name="Not Started" />
          </BarChart>
        </ResponsiveContainer>
      </FullWidthCard>

      {/* Active Courses */}
      <SectionTitle>Active Courses</SectionTitle>
      <CoursesGrid>
        {subscriptions.map((sub, index) => (
          <CourseCard 
            key={index}
            clickable
            onClick={() => onNavigateToPackage && sub.packageId && onNavigateToPackage(sub.packageId)}
          >
            <CourseHeader>
              <CourseTitle>{sub.packageName}</CourseTitle>
              <StatusBadge status={sub.status.toLowerCase().replace(' ', '-')}>
                {sub.status}
              </StatusBadge>
            </CourseHeader>
            
            <CourseProgress>
              <ProgressLabel>Progress</ProgressLabel>
              <ProgressValue>{sub.progress}%</ProgressValue>
            </CourseProgress>
            
            <ProgressBarContainer>
              <ProgressBar progress={sub.progress} />
            </ProgressBarContainer>
            
            <CourseStats>
              <CourseStat>
                <FiCheckCircle size={16} />
                {sub.videosCompleted} of {sub.totalVideos} videos
              </CourseStat>
              <CourseStat>
                <FiClock size={16} />
                {sub.watchHours} hours
              </CourseStat>
            </CourseStats>
            
            <CourseFooter>
              <ExpiryInfo>
                <FiCalendar size={14} />
                Expires in {sub.daysUntilExpiry} days
              </ExpiryInfo>
              <ContinueButton 
                onClick={(e) => {
                  e.stopPropagation();
                  if (onNavigateToPackage && sub.packageId) {
                    onNavigateToPackage(sub.packageId);
                  }
                }}
              >
                Continue Learning →
              </ContinueButton>
            </CourseFooter>
          </CourseCard>
        ))}
      </CoursesGrid>

      {/* Two Column Layout */}
      <TwoColumnGrid>
        {/* Upcoming Assignments */}
        <FullHeightCard>
          <CardTitle>Upcoming Assignments</CardTitle>
          <AssignmentsList>
            {assignments.map((assignment, index) => (
              <AssignmentItem key={index}>
                <AssignmentHeader>
                  <AssignmentTitle>{assignment.title}</AssignmentTitle>
                  <PriorityBadge priority={assignment.priority}>
                    {assignment.priority}
                  </PriorityBadge>
                </AssignmentHeader>
                <AssignmentMeta>
                  <span>{assignment.type}</span>
                </AssignmentMeta>
                <AssignmentDue>
                  <FiCalendar size={14} />
                  Due: {new Date(assignment.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </AssignmentDue>
              </AssignmentItem>
            ))}
            {assignments.length === 0 && (
              <EmptyState>No upcoming assignments</EmptyState>
            )}
          </AssignmentsList>
        </FullHeightCard>

        {/* Recent Achievements */}
        <FullHeightCard>
          <CardTitle>Recent Achievements</CardTitle>
          <AchievementsList>
            {achievements.map((achievement, index) => (
              <AchievementItem key={index}>
                <AchievementIcon>{achievement.achievement_icon}</AchievementIcon>
                <AchievementContent>
                  <AchievementName>{achievement.achievement_name}</AchievementName>
                  <AchievementDescription>
                    {achievement.achievement_description}
                  </AchievementDescription>
                  <AchievementTime>
                    {getTimeAgo(achievement.earned_at)}
                  </AchievementTime>
                </AchievementContent>
              </AchievementItem>
            ))}
            {achievements.length === 0 && (
              <EmptyState>No achievements yet - keep learning!</EmptyState>
            )}
          </AchievementsList>
        </FullHeightCard>
      </TwoColumnGrid>

      {/* Subscription Status Card */}
      {subscriptionStatus.hasActiveSubscription && (
        <SubscriptionCard>
          <SubscriptionHeader>
            <SubscriptionBadge>
              <FiAward size={20} />
              {subscriptionStatus.plan}
            </SubscriptionBadge>
            <SubscriptionStatus status={subscriptionStatus.status}>
              Active
            </SubscriptionStatus>
          </SubscriptionHeader>
          
          <SubscriptionExpiry>
            Valid until {new Date(subscriptionStatus.validUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </SubscriptionExpiry>
          
          <SubscriptionFeatures>
            <SubscriptionFeature>
              <FiCheckCircle color="#10b981" />
              Courses Access: <strong>{subscriptionStatus.coursesAccess}</strong>
            </SubscriptionFeature>
            <SubscriptionFeature>
              <FiCheckCircle color="#10b981" />
              Live Sessions: <strong>Included</strong>
            </SubscriptionFeature>
            <SubscriptionFeature>
              <FiCheckCircle color="#10b981" />
              Certificates: <strong>Enabled</strong>
            </SubscriptionFeature>
            <SubscriptionFeature>
              <FiCheckCircle color="#10b981" />
              Priority Support: <strong>24/7</strong>
            </SubscriptionFeature>
          </SubscriptionFeatures>
          
          <SubscriptionFooter>
            <div>Next billing date</div>
            <div style={{ fontSize: '20px', fontWeight: '700' }}>
              {subscriptionStatus.validUntil ? new Date(new Date(subscriptionStatus.validUntil).getTime() + 1).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
              ${subscriptionStatus.price}/month
            </div>
          </SubscriptionFooter>
        </SubscriptionCard>
      )}
    </DashboardContainer>
  );
};

// Helper function
function getTimeAgo(timestamp: string): string {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now.getTime() - past.getTime();
  
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffMs / 604800000);
  
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
}

export default StudentDashboard;

// Styled Components
const DashboardContainer = styled.div`
  padding: 0;
  max-width: 1400px;
  margin: 0 auto;
`;

const DashboardHeader = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  flex: 1;
`;

const TimeframeSelector = styled.div`
  display: flex;
  gap: 8px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
`;

const TimeframeButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    color: #3b82f6;
  }
`;

const ErrorBanner = styled.div`
  padding: 12px 16px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #92400e;
  font-size: 14px;
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const KPICard = styled.div<{ clickable?: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    ${props => props.clickable && `
      border-color: #3b82f6;
    `}
  }
`;

const KPIIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  flex-shrink: 0;
`;

const KPIContent = styled.div`
  flex: 1;
`;

const KPILabel = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const KPIValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  line-height: 1;
`;

const KPIChange = styled.div<{ trend: string }>`
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => {
    if (props.trend === 'up') return '#10b981';
    if (props.trend === 'down') return '#ef4444';
    return '#6b7280';
  }};
  font-weight: 600;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const FullWidthCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 32px;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 20px 0;
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const CourseCard = styled.div<{ clickable?: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    ${props => props.clickable && `
      border-color: #3b82f6;
    `}
  }
`;

const CourseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const CourseTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  flex: 1;
`;

const StatusBadge = styled.div<{ status: string }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    if (props.status === 'ahead') return '#d1fae5';
    if (props.status === 'on-track') return '#dbeafe';
    if (props.status === 'at-risk') return '#fee2e2';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.status === 'ahead') return '#065f46';
    if (props.status === 'on-track') return '#1e40af';
    if (props.status === 'at-risk') return '#991b1b';
    return '#374151';
  }};
`;

const CourseProgress = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ProgressLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const ProgressValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
  transition: width 0.3s ease;
`;

const CourseStats = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const CourseStat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
  
  svg {
    color: #3b82f6;
  }
`;

const CourseFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const ExpiryInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const ContinueButton = styled.button`
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2563eb;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FullHeightCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
`;

const AssignmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AssignmentItem = styled.div`
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const AssignmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const AssignmentTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
`;

const PriorityBadge = styled.div<{ priority: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${props => {
    if (props.priority === 'high') return '#fee2e2';
    if (props.priority === 'medium') return '#fef3c7';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.priority === 'high') return '#991b1b';
    if (props.priority === 'medium') return '#92400e';
    return '#374151';
  }};
`;

const AssignmentMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
  text-transform: capitalize;
`;

const AssignmentDue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  
  svg {
    color: #3b82f6;
  }
`;

const AchievementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AchievementItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const AchievementIcon = styled.div`
  font-size: 32px;
  flex-shrink: 0;
`;

const AchievementContent = styled.div`
  flex: 1;
`;

const AchievementName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const AchievementDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
`;

const AchievementTime = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const SubscriptionCard = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 16px;
  padding: 32px;
  color: white;
  margin-bottom: 32px;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
`;

const SubscriptionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SubscriptionBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 24px;
  font-weight: 700;
`;

const SubscriptionStatus = styled.div<{ status: string }>`
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(10px);
`;

const SubscriptionExpiry = styled.div`
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 24px;
`;

const SubscriptionFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
  padding: 20px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const SubscriptionFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  
  svg {
    flex-shrink: 0;
  }
  
  strong {
    font-weight: 600;
  }
`;

const SubscriptionFooter = styled.div`
  text-align: left;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  
  p {
    color: #6b7280;
    font-size: 16px;
  }
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: #ef4444;
  font-size: 16px;
`;

const EmptyState = styled.div`
  padding: 32px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

